import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { broadcastLogout, clearSessionActivity, markActivity } from "../lib/sessionActivity";

const AuthContext = createContext(null);

// Persist a lightweight "we have a live session" flag across tab
// reopens. Direct-URL navigation (paste `/tools/sales-estimator` into
// a new tab) used to blank the layout for the ~500ms /auth/me round
// trip because <Protected> returns a spinner while status ==
// "checking". We now read this flag synchronously on mount and seed
// the initial status so the shell (sidebar, header, breadcrumbs)
// paints instantly. `/auth/me` still runs in the background and
// downgrades status to "anonymous" if the cookie has expired.
const AUTH_HINT_KEY = "cpg.auth.hint";
function readAuthHint() {
  try { return window.localStorage.getItem(AUTH_HINT_KEY) === "1"; }
  catch { return false; }
}
function writeAuthHint(v) {
  try {
    if (v) window.localStorage.setItem(AUTH_HINT_KEY, "1");
    else window.localStorage.removeItem(AUTH_HINT_KEY);
  } catch { /* private mode */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Optimistic bootstrap: if a previous tab confirmed a live session,
  // start as "authenticated" so <Protected> renders <AppShell> right
  // away. `/auth/me` runs in useEffect and corrects us if the cookie
  // was rotated out.
  const [status, setStatus] = useState(() =>
    readAuthHint() ? "authenticated" : "checking",
  );

  const refresh = useCallback(async () => {
    // If we're returning from Emergent Google auth, let the callback route handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setStatus("checking");
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus("authenticated");
      writeAuthHint(true);
    } catch {
      setUser(null);
      setStatus("anonymous");
      writeAuthHint(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Back/forward cache restores a frozen page — re-validate the session
    // so a signed-out or expired user can't keep using a cached page.
    const onPageShow = (e) => { if (e.persisted) refresh(); };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    markActivity(true);
    setUser(data.user);
    setStatus("authenticated");
    writeAuthHint(true);
    return data.user;
  };

  const register = async (email, password, name, phone_e164, tokens = {}) => {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const ref = params.get("ref") || undefined;
    // Backend attributes the referral + credits the referrer atomically inside /auth/register.
    const { data } = await api.post("/auth/register", {
      email,
      password,
      name,
      phone_e164: phone_e164 || null,
      ref,
      email_verification_token: tokens.email || undefined,
      phone_verification_token: tokens.phone || undefined,
    });
    markActivity(true);
    setUser(data.user);
    setStatus("authenticated");
    writeAuthHint(true);
    return data.user;
  };

  // callApi=false when another tab already ended the session.
  const logout = async ({ callApi = true, reason = "logout" } = {}) => {
    if (callApi) {
      broadcastLogout(reason);
      try {
        await api.post("/auth/logout");
      } catch {}
    }
    clearSessionActivity();
    setUser(null);
    setStatus("anonymous");
    writeAuthHint(false);
  };

  const value = { user, status, login, register, logout, refresh, setUser, setStatus };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
